import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Chip,
  Typography,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";

import PHCOnboardingNotCompletedContainer from "./PHCOnboardingNotCompletedContainer";
import GraphDashboard from "./GraphDashboard";
import UploadPortfolio from "../Onboarding/UploadPreview/UploadPortfolio";
import PreviewPortfolio from "../Onboarding/UploadPreview/PreviewPortfolio";
import HealthCheckSaveModal from "components/Dashboard/HealthCheckSaveModal";
import IconButton from "components/common/IconButton";
import Modal from "components/common/Modal";
import JarvisButton from "components/JarvisButton";

import { PHCDashboardAction } from "stores/PHC/PHCDashoard/PHCDashboardAction";
import { PHCDashboardApis } from "stores/PHC/PHCDashoard/PHCDashboardApis";
import { PHCOnboardingApis } from "stores/PHC/PHCOnboarding/PHCOnboardingApis";
import { PHCOnboardingActions } from "stores/PHC/PHCOnboarding/PHCOnboardingAction";
import { commonActions } from "stores/common/commonActions";

function Dashboard() {
  const dispatch = useDispatch();

  /* ---------------- REDUX STATE ---------------- */
  const {
    list: portfolios,
    loading,
    tags,
    activePortfolioId,
    currentView,
    justUploadedPortfolioId,
    nonExistentPortfolios,
    isFromOneMoney,
  } = useSelector((state) => state.PHC.PHCDashboard);

  const isPHCOnboardingCompleted = useSelector(
    (state) => state.PHC.PHCOnboarding.isOnboardingCompleted,
  );

  /* ---------------- LOCAL UI STATE ---------------- */
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [chipFadeVisible, setChipFadeVisible] = useState(false);
  const chipRefs = useRef({});
  const chipScrollerRef = useRef(null);

  useEffect(() => {
      dispatch(PHCOnboardingActions.getOnboardingData()); 
  }, []);

  /* ---------------- FETCH PORTFOLIOS ---------------- */
  useEffect(() => {
    dispatch(PHCDashboardAction.fetchPortfolioRequest());
  }, []);

  /* ---------------- BUILD TAGS FROM BACKEND ---------------- */
  useEffect(() => {
    if (loading || !portfolios?.length) return;

    const isActiveValid = portfolios.some(
      (p) => p.portfolioId === activePortfolioId,
    );

    let activeId = isActiveValid ? activePortfolioId : null;
    let view = null;

    // OneMoney / excel upload: keep preview table even if list refresh races.
    // Do not reset the view when a portfolio is already selected — Continue
    // and chip clicks set the screen themselves.
    if (justUploadedPortfolioId) {
      activeId = justUploadedPortfolioId;
      view = "preview";
    } else if (!activeId) {
      const pending = portfolios.find((p) => p.updatedAt === null);
      if (pending) {
        activeId = pending.portfolioId;
        view = "preview";
      } else {
        activeId = portfolios[0].portfolioId;
        view = "graph";
      }
    }

    const updatedTags = portfolios.map((p) => ({
      id: p.portfolioId,
      name: p.portfolioName,
      updatedAt: p.updatedAt,
      isActive: p.portfolioId === activeId,
      portfolioData: p,
    }));

    dispatch(PHCDashboardAction.setTags(updatedTags));
    dispatch(PHCDashboardAction.setActivePortfolio(activeId));
    if (view) {
      dispatch(PHCDashboardAction.setCurrentView(view));
    }
  }, [portfolios, loading, justUploadedPortfolioId]);

  useEffect(() => {
    const el = chipScrollerRef.current;
    if (!el) return undefined;

    const updateFade = () => {
      setChipFadeVisible(
        el.scrollWidth - el.clientWidth - el.scrollLeft > 4,
      );
    };

    updateFade();
    el.addEventListener("scroll", updateFade, { passive: true });
    const observer = new ResizeObserver(updateFade);
    observer.observe(el);
    return () => {
      el.removeEventListener("scroll", updateFade);
      observer.disconnect();
    };
  }, [tags, isPHCOnboardingCompleted]);

  useEffect(() => {
    const activeIndex = tags.findIndex((t) => t.isActive);
    if (activeIndex < 0) return;
    const active = tags[activeIndex];
    const chipKey = active.id ?? `temp-${activeIndex}`;
    chipRefs.current[chipKey]?.scrollIntoView?.({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [activePortfolioId, tags]);

  /* ---------------- ADD NEW PORTFOLIO ---------------- */
  const handleAddNewPortfolio = () => {
    const newTag = {
      id: null,
      name: `New Portfolio ${tags.length + 1}`,
      isTemp: true,
      isExisting: false,
      isActive: true,
      updatedAt: null,
    };

    const updatedTags = [
      ...tags.map((t) => ({ ...t, isActive: false })),
      newTag,
    ];

    dispatch(PHCDashboardAction.setTags(updatedTags));
    dispatch(PHCDashboardAction.setActivePortfolio(null));
    dispatch(PHCDashboardAction.setCurrentView("upload"));
    dispatch(PHCDashboardAction.setJustUploadedPortfolio(null));
    dispatch(PHCDashboardAction.setCompletedPortfolio(null));
    dispatch(PHCDashboardAction.setFromOneMoney(false));
    dispatch(
      PHCOnboardingActions.updatePHCOnboardingData({
        portfolioId: null,
        consentId: null,
      }),
    );
  };

  /* ---------------- PORTFOLIO CREATED ---------------- */
  const handlePortfolioCreated = (id) => {
    dispatch(PHCDashboardAction.setJustUploadedPortfolio(id));
    dispatch(PHCDashboardAction.setActivePortfolio(id));
    dispatch(PHCDashboardAction.setCurrentView("preview"));
    dispatch(PHCDashboardAction.fetchPortfolioRequest());
  };

  /* ---------------- TAB CLICK ---------------- */
  const handlePortfolioTabClick = (id, updatedAt) => {
    if (updatedAt === null) {
      dispatch(PHCDashboardAction.setCurrentView("preview"));
    } else {
      dispatch(PHCDashboardAction.setCurrentView("graph"));
    }

    const updatedTags = tags.map((t) => ({
      ...t,
      isActive: t.id === id,
    }));

    dispatch(PHCDashboardAction.setTags(updatedTags));
    dispatch(PHCDashboardAction.setActivePortfolio(id));
    dispatch(PHCDashboardAction.setJustUploadedPortfolio(null));
    dispatch(
      PHCOnboardingActions.updatePHCOnboardingData({
        portfolioId: null,
      }),
    );
  };

  /* ---------------- DELETE FLOW ---------------- */
  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await PHCDashboardApis.deletePortfolio(portfolioToDelete.id);

      dispatch(
        PHCDashboardAction.addNonExistentPortfolio(portfolioToDelete.id),
      );

      dispatch(
        commonActions.setToast({
          message: "Portfolio deleted successfully",
          type: "success",
        }),
      );

      dispatch(PHCDashboardAction.fetchPortfolioRequest());
    } catch (e) {
      dispatch(
        commonActions.setToast({
          message: "Failed to delete portfolio",
          type: "error",
        }),
      );
    } finally {
      setIsDeleting(false);
      setDeleteDialogOpen(false);
      setPortfolioToDelete(null);
    }
  };

  /* ---------------- PREVIEW COMPLETED ---------------- */
  const handlePreviewCompleted = () => {
    dispatch(PHCDashboardAction.setJustUploadedPortfolio(null));
    dispatch(PHCDashboardAction.setCurrentView("graph"));
  };

  /* ---------------- SAVE NAME ---------------- */
  const handleSaveName = async (name, id) => {
    await PHCOnboardingApis.saveReport({
      portfolioId: id,
      portfolioName: name,
    });

    dispatch(PHCOnboardingActions.getOnboardingData());
    dispatch(PHCDashboardAction.fetchPortfolioRequest());
    setModalOpen(false);
  };

  /* ---------------- RENDER VIEW ---------------- */
  const renderCurrentView = () => {
    if (!activePortfolioId && currentView !== "upload") {
      return <UploadPortfolio getportfolioId={handlePortfolioCreated} />;
    }

    if (currentView === "upload") {
      return <UploadPortfolio getportfolioId={handlePortfolioCreated} />;
    }

    if (currentView === "preview") {
      return (
        <PreviewPortfolio
          onNextStep={handlePreviewCompleted}
          isFromOneMoney={isFromOneMoney}
          onNoHoldings={() => {
            dispatch(PHCDashboardAction.setCurrentView("graph"));
            dispatch(PHCDashboardAction.setActivePortfolio(null));
          }}
        />
      );
    }

    return (
      <GraphDashboard
        openModal={() => setModalOpen(true)}
        portfolioId={activePortfolioId}
        shouldRegenerate={Boolean(
          tags.find((t) => t.id === activePortfolioId)?.updatedAt,
        )}
      />
    );
  };

  const activeTag = tags.find((t) => t.isActive);

  if (!isPHCOnboardingCompleted) {
    return <PHCOnboardingNotCompletedContainer />;
  }

  const isGraphView =
    Boolean(activePortfolioId) &&
    currentView !== "upload" &&
    currentView !== "preview";

  return (
    <>
      {/* TAGS — chips scroll, add stays pinned; GraphDashboard is full-bleed */}
      <Box sx={{ px: { xs: 2, md: 3 }, pt: 1, pb: 0.5 }}>
        <Box
          display="flex"
          alignItems="center"
          gap={1}
        >
          <Box sx={{ position: "relative", flex: 1, minWidth: 0 }}>
            <Box
              ref={chipScrollerRef}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                overflowX: "auto",
                py: 0.5,
                pr: 2,
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "&::-webkit-scrollbar": { display: "none" },
              }}
            >
              {tags.map((tag, i) => {
                const canDelete = tag.updatedAt === null && tag.id;
                const chipKey = tag.id ?? `temp-${i}`;
                return (
                  <Box
                    key={chipKey}
                    ref={(el) => {
                      if (el) chipRefs.current[chipKey] = el;
                    }}
                    sx={{ flexShrink: 0, maxWidth: { xs: 168, sm: 200, md: 240 } }}
                  >
                    <Chip
                      label={tag.name}
                      clickable
                      onClick={() =>
                        handlePortfolioTabClick(tag.id, tag.updatedAt)
                      }
                      onDelete={
                        canDelete
                          ? () => {
                              setPortfolioToDelete(tag);
                              setDeleteDialogOpen(true);
                            }
                          : undefined
                      }
                      deleteIcon={<CloseIcon />}
                      sx={{
                        maxWidth: "100%",
                        height: 36,
                        borderRadius: "999px",
                        backgroundColor: tag.isActive ? "#2977EC" : "#F8FAFC",
                        border: tag.isActive
                          ? "1px solid #2977EC"
                          : "1px solid #E5E7EB",
                        color: tag.isActive ? "#FFFFFF" : "#111827",
                        fontWeight: tag.isActive ? 600 : 500,
                        cursor: "pointer",
                        "& .MuiChip-label": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          px: 1.5,
                        },
                        "& .MuiChip-deleteIcon": {
                          fontSize: 16,
                          color: tag.isActive
                            ? "rgba(255,255,255,0.85)"
                            : "#6B7280",
                          mr: 0.5,
                        },
                        "&:hover": {
                          backgroundColor: tag.isActive ? "#1D6AD6" : "#F1F5F9",
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Box>
            {chipFadeVisible ? (
              <Box
                aria-hidden
                sx={{
                  pointerEvents: "none",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  bottom: 0,
                  width: 28,
                  background:
                    "linear-gradient(to right, rgba(255,255,255,0), #FFFFFF)",
                }}
              />
            ) : null}
          </Box>

          <Box
            display="flex"
            alignItems="center"
            gap={0.75}
            sx={{ flexShrink: 0 }}
          >
            <IconButton
              aria-label="Add portfolio"
              onClick={handleAddNewPortfolio}
              sx={{
                width: 40,
                height: 40,
                bgcolor: "#E8F4FF",
                color: "#2977EC",
                "&:hover": { bgcolor: "#D6EBFF" },
              }}
            >
              <AddIcon />
            </IconButton>

            {tags.length > 0 ? (
              <Box
                sx={{
                  height: 32,
                  px: 1.25,
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  borderRadius: "999px",
                  bgcolor: "#EEF4FF",
                  border: "1px solid #D6E6FA",
                  whiteSpace: "nowrap",
                }}
              >
                <Typography
                  component="span"
                  sx={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "#2977EC",
                    lineHeight: 1,
                  }}
                >
                  {tags.length}
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#6B7280",
                    lineHeight: 1,
                  }}
                >
                  {tags.length === 1 ? "report" : "reports"}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Box>
      </Box>

      {/* for PHC only removed extra paddings around because theme is dark */}
      <Box
        sx={
          isGraphView
            ? undefined
            : { px: { xs: 2, md: 3 }, pb: { xs: 2, md: 3 } }
        }
      >
        {renderCurrentView()}
      </Box>

      <HealthCheckSaveModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        id={activeTag?.id}
        tagName={activeTag?.name}
        onSave={handleSaveName}
      />

      <Modal open={deleteDialogOpen} disableClose>
        <DialogTitle>Delete Portfolio</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this portfolio?
        </DialogContent>
        <DialogActions>
          <JarvisButton onClick={() => setDeleteDialogOpen(false)} disableEndIcon >
            Cancel
          </JarvisButton>
          <JarvisButton
            color="error"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
            disableEndIcon
          >
            Delete
          </JarvisButton>
        </DialogActions>
      </Modal>
    </>
  );
}

export default Dashboard;
